import { useState } from "react";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding } from "@/hooks/use-branding";
import { apiRequest } from "@/lib/queryClient";
import { Bot, ArrowLeft, MailCheck } from "lucide-react";

/**
 * /forgot-password — request a password reset link. Works for every
 * password-based user type (members, org admins, etc.). Always reports
 * success regardless of whether the email exists (no account enumeration).
 * In dev with no email transport, the API returns a devToken so the reset
 * can be completed without a real inbox.
 */
export default function ForgotPasswordPage() {
  const branding = useBranding();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await apiRequest("POST", "/api/auth/forgot-password", { email: email.trim() });
      const body = await res.json().catch(() => ({}));
      setSent(true);
      if (body?.devToken) {
        setDevLink(
          `/reset-password?email=${encodeURIComponent(email.trim())}&token=${encodeURIComponent(body.devToken)}`,
        );
      }
    } catch {
      // Even on error we show the neutral confirmation to avoid leaking
      // whether the address is registered.
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-50 to-white px-4 py-10">
      <div className="absolute inset-x-0 top-0 -z-10 h-[360px] bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.10),_transparent_60%)]" />
      <div className="w-full max-w-md">
        <Link href="/" className="flex items-center justify-center gap-2 mb-6" data-testid="link-home">
          <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 grid place-items-center text-white shadow-sm">
            <Bot className="w-5 h-5" />
          </span>
          <span className="font-semibold tracking-tight text-lg text-slate-900">{branding.shortName}</span>
        </Link>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            {sent ? (
              <div className="space-y-4 text-center">
                <span className="mx-auto w-12 h-12 rounded-full bg-emerald-50 grid place-items-center text-emerald-600">
                  <MailCheck className="w-6 h-6" />
                </span>
                <h2 className="text-xl font-semibold" data-testid="text-forgot-sent-title">Check your email</h2>
                <p className="text-sm text-slate-500">
                  If an account exists for that address, we've sent a link to reset your password.
                </p>
                {devLink && (
                  <Link
                    href={devLink}
                    className="inline-block text-sm font-medium text-indigo-600 hover:underline"
                    data-testid="link-dev-reset"
                  >
                    Dev: open reset link →
                  </Link>
                )}
                <Link href="/login" className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700 pt-2" data-testid="link-back-login">
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center space-y-2 mb-5">
                  <h2 className="text-2xl font-semibold" data-testid="text-forgot-title">Forgot your password?</h2>
                  <p className="text-sm text-slate-500">
                    Enter your account email and we'll send you a link to reset it.
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="username"
                      placeholder="you@company.com"
                      data-testid="input-forgot-email"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="button-forgot-submit"
                  >
                    {submitting ? "Sending…" : "Send reset link"}
                  </Button>
                </form>
                <Link href="/login" className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700 pt-4" data-testid="link-back-login">
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
